import { Alert, Button, Divider, Stack, TextField, Typography } from '@mui/material';
import { GoogleLogin } from 'components/login/GoogleLogin';
import { MicrosoftLogin } from 'components/login/MicrosoftLogin';
import { observer } from 'mobx-react-lite';
import { KeyboardEvent as ReactKeyboardEvent, useMemo } from 'react';
import { LinkButton } from 'components/shared/LinkButton';
import { CenteredFormPage } from 'components/CenteredFormPage';
import { useRootStore } from 'stores/RootStore';

const LoginPage = observer(() => {
  const { routeStore, authStore, socialAuthStore, loginStore } = useRootStore();
  const { navigateByName } = routeStore;
  const { status } = authStore;
  const { form, message, login } = loginStore;
  const { message: socialMessage, azureLogin } = socialAuthStore;
  const displayMessage = message || socialMessage;
  const googleLoginOptions = useMemo(() => ({ theme: 'outline', logo_alignment: 'center' }), []);

  const handleKeyInField = (evt: ReactKeyboardEvent) => {
    if (evt.key === 'Enter') {
      evt.stopPropagation();
      evt.preventDefault();
      if (form.isValid) {
        login();
      }
    }
  };

  return (
    <CenteredFormPage aria-label="login page">
      <form>
        <Stack spacing={2} sx={{ margin: '32px 0' }}>
          <Typography variant="h5" textAlign="center" style={{ marginBottom: displayMessage ? undefined : '32px' }}>
            <strong>Log In</strong>
          </Typography>
          {displayMessage && (
            <Alert aria-label="login-error" severity="error">
              {displayMessage}
            </Alert>
          )}
          {status === 'valid' && <span aria-label="login-success"></span>}
          <GoogleLogin options={googleLoginOptions} fullWidth />
          <MicrosoftLogin onClick={() => azureLogin()} fullWidth />
          <Divider sx={{ marginTop: '32px !important', marginBottom: '32px !important' }}>
            <Typography variant="body2" sx={{ color: '#22222261' }}>
              OR
            </Typography>
          </Divider>
          <TextField
            variant="outlined"
            autoComplete="username"
            onKeyDown={handleKeyInField}
            {...form.$('email').bind()}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            variant="outlined"
            autoComplete="current-password"
            onKeyDown={handleKeyInField}
            {...form.$('password').bind()}
            inputProps={{
              role: 'textbox',
            }}
            InputLabelProps={{ shrink: true }}
          />
          <Typography variant="body1" alignSelf="center" color="text.secondary" sx={{ marginTop: '32px !important' }}>
            By continuing you agree to Bowery's{' '}
            <LinkButton onClick={() => navigateByName('loginTerms')}>Terms of Use.</LinkButton>
          </Typography>
          <Button
            variant="contained"
            disableElevation
            disabled={!form.isValid}
            style={{ height: 56 }}
            onClick={() => login()}
          >
            Log In
          </Button>
          <Typography variant="body1" alignSelf="center" color="text.secondary" style={{ marginTop: '32px' }}>
            Don't have an account?{' '}
            <LinkButton onClick={() => navigateByName('register')}>Create an account.</LinkButton>
          </Typography>
          <Typography variant="body1" alignSelf="center" color="text.secondary">
            Need help? <LinkButton onClick={() => navigateByName('loginContact')}>Contact Support.</LinkButton>
          </Typography>
          <Typography variant="body1" alignSelf="center" color="text.secondary">
            <LinkButton onClick={() => navigateByName('resetPasswordEmail')}>Forgot Password?</LinkButton>
          </Typography>
        </Stack>
      </form>
    </CenteredFormPage>
  );
});

export { LoginPage };
